import React, { useState, useEffect } from "react";
import '../../assets/styles/ClientShared.css';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { db, auth } from "../../firebase-config";

const Launch = () => {
  const [finalPaymentMade, setFinalPaymentMade] = useState(false);
  const [loading, setLoading] = useState(true);
  const [finalPaymentLink, setFinalPaymentLink] = useState("");
  const [videoTutorialLink, setVideoTutorialLink] = useState("");
  const [websiteURL, setWebsiteURL] = useState("");
  const [feedback, setFeedback] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [searchParams] = useSearchParams();

  const supportEmail = "nextlvlwebsites@gmail.com";
  const googleTutorialLink = "https://support.google.com/business/answer/3038177";

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) throw new Error("User document not found.");

        const userData = userDocSnap.data();
        if (!userData.projectId) throw new Error("Project ID not found.");

        const projectDocRef = doc(db, "projects", userData.projectId);
        const projectDocSnap = await getDoc(projectDocRef);

        if (!projectDocSnap.exists()) throw new Error("Project document not found.");

        const projectData = projectDocSnap.data();
        setFinalPaymentMade(projectData.finalPaymentMade || false);
        setFinalPaymentLink(projectData.finalPaymentLink || "");
        setVideoTutorialLink(projectData.videoTutorialLink || "");
        setWebsiteURL(projectData.websiteURL || "");

        const paymentSuccess = searchParams.get("success");
        if (paymentSuccess === "true" && !projectData.finalPaymentMade) {
          await updateDoc(projectDocRef, { finalPaymentMade: true });
          setFinalPaymentMade(true);
          toast.success("Payment confirmed! You can now proceed to the next steps.");
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching project data:", error.message);
        toast.error("Failed to load project data. Please try again.");
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [searchParams]);

  const handleFeedbackSubmit = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("User not authenticated.");

      const userDocRef = doc(db, "users", currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) throw new Error("User document not found.");

      const userData = userDocSnap.data();
      if (!userData.projectId) throw new Error("Project ID not found.");

      const projectDocRef = doc(db, "projects", userData.projectId);
      await updateDoc(projectDocRef, { feedback });

      setFeedbackSubmitted(true);
      toast.success("Thank you for your feedback!");
    } catch (error) {
      console.error("Error submitting feedback:", error.message);
      toast.error("Failed to submit feedback. Please try again.");
    }
  };

  const handlePaymentRedirect = () => {
    if (finalPaymentLink) {
      window.open(
        finalPaymentLink.startsWith("http://") || finalPaymentLink.startsWith("https://")
          ? finalPaymentLink
          : `http://${finalPaymentLink}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      toast.error("Final payment link is not available. Please contact support.");
    }
  };

  if (loading) {
    return <div className="cp-loading"><p>Loading...</p></div>;
  }

  return (
    <>
      <ToastContainer />
      {!finalPaymentMade ? (
        <div className="cp-page cp-page--narrow">
          <div className="cp-page-header">
            <p className="cp-eyebrow">Client Portal</p>
            <h1 className="cp-title">Final Payment</h1>
            <p className="cp-subtitle">Complete the final payment to launch your website.</p>
          </div>
          <div className="cp-section">
            <div className="cp-section-header"><h2 className="cp-section-title">Pay the Remaining Balance</h2></div>
            <div className="cp-section-body">
              <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.7,marginTop:0}}>
                The final balance must be paid to proceed with launch. Once payment is confirmed, you'll gain access to next steps.
              </p>
              <div className="cp-btn-row">
                <button className="cp-btn-primary" onClick={handlePaymentRedirect}>Pay Now →</button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="cp-page cp-page--narrow">
          <div className="cp-page-header">
            <p className="cp-eyebrow">Client Portal — Launch</p>
            <h1 className="cp-title">Your site is live.</h1>
            <p className="cp-subtitle">Congratulations! Your website is now live and ready for the world.</p>
          </div>

          {/* View website */}
          <div className="cp-section" style={{marginBottom:'20px'}}>
            <div className="cp-section-header"><h2 className="cp-section-title">Your Website</h2></div>
            <div className="cp-section-body">
              <a href={websiteURL.startsWith('http')?websiteURL:`http://${websiteURL}`} target="_blank" rel="noopener noreferrer" className="cp-btn-primary" style={{display:'inline-block',textDecoration:'none'}}>View Website →</a>
            </div>
          </div>

          {/* Next steps */}
          <div className="cp-section" style={{marginBottom:'20px'}}>
            <div className="cp-section-header"><h2 className="cp-section-title">Next Steps</h2></div>
            <div className="cp-section-body">
              <ul className="cp-list">
                <li className="cp-list-item"><strong>Firebase Access:</strong> Access details will be shared with you shortly.</li>
                <li className="cp-list-item"><strong>GitHub Repository:</strong> Source code is available privately on GitHub. Access details coming soon.</li>
                <li className="cp-list-item"><strong>Google Business:</strong> <a href={googleTutorialLink.startsWith('http')?googleTutorialLink:`https://${googleTutorialLink}`} target="_blank" rel="noopener noreferrer" className="cp-doc-link">Follow this tutorial</a> to add your site to Google Business.</li>
                <li className="cp-list-item"><strong>Video Walkthrough:</strong> <a href={videoTutorialLink.startsWith('http')?videoTutorialLink:`https://${videoTutorialLink}`} target="_blank" rel="noopener noreferrer" className="cp-doc-link">Watch the Loom video</a> for a full walkthrough.</li>
                <li className="cp-list-item"><strong>Post-Launch Support:</strong> 1 month free. Reach out at <a href={`mailto:${supportEmail}`} className="cp-doc-link">{supportEmail}</a>.</li>
              </ul>
            </div>
          </div>

          {/* Feedback */}
          <div className="cp-section" style={{marginBottom:'24px'}}>
            <div className="cp-section-header"><h2 className="cp-section-title">Share Your Feedback</h2></div>
            <div className="cp-section-body">
              <div className="cp-form-group">
                <label>Your experience with our service</label>
                <textarea value={feedback} onChange={e=>setFeedback(e.target.value)} placeholder="Write your feedback here..." />
              </div>
              <div className="cp-btn-row">
                <button className="cp-btn-primary" onClick={handleFeedbackSubmit} disabled={feedbackSubmitted}>
                  {feedbackSubmitted ? 'Feedback Submitted' : 'Submit Feedback'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Launch;
